import React from "react";
import { Package, Truck, Ship } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const formatNumber = (value, decimals = 2) => {
  return (value || 0).toLocaleString('es-PE', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

const formatCurrency = (value, currency = "USD") => {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2
  }).format(value || 0);
};

export const InstallationCard = ({ installation, setInstallation, installationTotal, totalM2 = 0 }) => {
  // Configuration values (rows 1-3 from Excel - blue numbers)
  const emoTotal = parseFloat(installation.emo_total) || 120;  // EMO total
  const eppsXPer = parseFloat(installation.epps_x_per) || 100;  // EPPS X PER
  const unidDesta = parseFloat(installation.unid_desta) || 20;  // UNID DESTA (BOT SIL)
  const canPers = parseFloat(installation.can_pers) || 6;  // CAN. PERS (cantidad de personal)
  const selloTotal = parseFloat(installation.sello_total) || 6580;  // SELLO total
  
  // Calculate values per m2 based on total m2 of project
  const projectM2 = totalM2 || 1;  // Avoid division by zero
  const emoXM2 = (canPers * emoTotal) / projectM2;
  const destaXM2 = (canPers * unidDesta) / projectM2;
  const eppXM2 = (canPers * eppsXPer) / projectM2;
  const selloXM2 = selloTotal / projectM2;
  
  // Other values
  const cosM2i = parseFloat(installation.cos_m2i) || 123;
  const adicional = parseFloat(installation.adicional) || 0;
  const utiPercent = parseFloat(installation.uti_percent) || 11;
  
  // Base cost per m2 (COS FIN M2)
  const baseCostM2 = cosM2i + emoXM2 + selloXM2 + destaXM2 + eppXM2;
  
  return (
    <Card className="border-green-200 bg-green-50/30">
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2 text-green-700">
          <Package className="w-4 h-4" />
          Instalación (Soles)
        </CardTitle>
        <CardDescription className="text-xs">
          Total M²: {projectM2.toFixed(2)} | CAN.PERS: {canPers}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Row 1-3 Configuration (Blue values from Excel) */}
        <div className="p-2 bg-blue-50 rounded border border-blue-200">
          <Label className="text-xs font-semibold text-blue-700 mb-2 block">Configuración Base (Filas 1-3)</Label>
          <div className="grid grid-cols-5 gap-2">
            <div>
              <Label className="text-[10px] text-blue-600">EMO (S/.)</Label>
              <Input
                type="number"
                step="0.01"
                value={installation.emo_total || "120"}
                onChange={(e) => setInstallation({
                  ...installation,
                  emo_total: parseFloat(e.target.value) || 0
                })}
                className="h-6 text-xs border-blue-300"
              />
            </div>
            <div>
              <Label className="text-[10px] text-blue-600">EPPS X PER</Label>
              <Input
                type="number"
                step="0.01"
                value={installation.epps_x_per || "100"}
                onChange={(e) => setInstallation({
                  ...installation,
                  epps_x_per: parseFloat(e.target.value) || 0
                })}
                className="h-6 text-xs border-blue-300"
              />
            </div>
            <div>
              <Label className="text-[10px] text-blue-600">UNID DESTA</Label>
              <Input
                type="number"
                step="0.01"
                value={installation.unid_desta || "20"}
                onChange={(e) => setInstallation({
                  ...installation,
                  unid_desta: parseFloat(e.target.value) || 0
                })}
                className="h-6 text-xs border-blue-300"
              />
            </div>
            <div>
              <Label className="text-[10px] text-blue-600">CAN. PERS</Label>
              <Input
                type="number"
                step="1"
                value={installation.can_pers || "6"}
                onChange={(e) => setInstallation({
                  ...installation,
                  can_pers: parseInt(e.target.value) || 0
                })}
                className="h-6 text-xs border-blue-300"
              />
            </div>
            <div>
              <Label className="text-[10px] text-blue-600">SELLO Total</Label>
              <Input
                type="number"
                step="0.01"
                value={installation.sello_total || "6580"}
                onChange={(e) => setInstallation({
                  ...installation,
                  sello_total: parseFloat(e.target.value) || 0
                })}
                className="h-6 text-xs border-blue-300"
              />
            </div>
          </div>
        </div>
        
        {/* Calculated values per m2 (read-only) */}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-xs">COS M2i</Label>
            <Input
              type="number"
              step="0.01"
              value={installation.cos_m2i || "123"}
              onChange={(e) => setInstallation({
                ...installation,
                cos_m2i: parseFloat(e.target.value) || 0
              })}
              className="h-7 text-xs"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">EMO X M2</Label>
            <Input
              type="text"
              value={emoXM2.toFixed(2)}
              readOnly
              className="h-7 text-xs bg-slate-100"
              title={`= (${canPers} × ${emoTotal}) / ${projectM2.toFixed(2)}`}
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">SELLO X M2</Label>
            <Input
              type="text"
              value={selloXM2.toFixed(2)}
              readOnly
              className="h-7 text-xs bg-slate-100"
              title={`= ${selloTotal} / ${projectM2.toFixed(2)}`}
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">DESTA X M2</Label>
            <Input
              type="text"
              value={destaXM2.toFixed(2)}
              readOnly
              className="h-7 text-xs bg-slate-100"
              title={`= (${canPers} × ${unidDesta}) / ${projectM2.toFixed(2)}`}
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">EPP X M2</Label>
            <Input
              type="text"
              value={eppXM2.toFixed(2)}
              readOnly
              className="h-7 text-xs bg-slate-100"
              title={`= (${canPers} × ${eppsXPer}) / ${projectM2.toFixed(2)}`}
            />
          </div>
          <div>
            <Label className="text-xs">% UTI</Label>
            <Input
              type="number"
              step="0.1"
              value={installation.uti_percent || "11"}
              onChange={(e) => setInstallation({
                ...installation,
                uti_percent: parseFloat(e.target.value) || 0
              })}
              className="h-7 text-xs"
            />
          </div>
        </div>
        
        {/* ACC SOFT / ADICIONAL - Campo destacado */}
        <div className="p-2 border-2 border-orange-300 bg-orange-50 rounded-lg">
          <Label className="text-xs font-semibold text-orange-700">ACC SOFT (ADICIONAL)</Label>
          <Input
            type="number"
            step="0.01"
            value={installation.adicional || ""}
            onChange={(e) => setInstallation({
              ...installation,
              adicional: parseFloat(e.target.value) || 0
            })}
            className="h-8 text-sm border-orange-300 bg-white font-medium"
            placeholder="5000.00"
            data-testid="installation-adicional"
          />
        </div>
        
        {/* Summary */}
        <div className="text-xs space-y-1 pt-2 border-t">
          <div className="flex justify-between text-slate-600">
            <span>COS FIN M2 (base):</span>
            <span className="font-mono">S/ {baseCostM2.toFixed(2)}</span>
          </div>
          {adicional > 0 && (
            <div className="flex justify-between text-orange-600">
              <span>+ Adicional:</span>
              <span className="font-mono">S/ {adicional.toFixed(2)}</span>
            </div>
          )}
        </div>
        <div className="text-lg font-bold text-green-600">
          S/. {formatNumber(installationTotal)}
        </div>
      </CardContent>
    </Card>
  );
};

export const PDRCard = ({ pdr, setPdr, pdrTotal }) => {
  return (
    <Card className="border-orange-200 bg-orange-50/30">
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2 text-orange-700">
          <Truck className="w-4 h-4" />
          PDR - Prevencionista de Riesgo (Soles)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Cantidad</Label>
            <Input
              type="number"
              min="0"
              value={pdr.quantity || ""}
              onChange={(e) => setPdr({
                ...pdr,
                quantity: parseInt(e.target.value) || 0
              })}
              className="h-8"
            />
          </div>
          <div>
            <Label className="text-xs">Valor Unitario (S/.)</Label>
            <Input
              type="number"
              step="0.01"
              value={pdr.unit_value || ""}
              onChange={(e) => setPdr({
                ...pdr,
                unit_value: parseFloat(e.target.value) || 0
              })}
              className="h-8"
            />
          </div>
        </div>
        <div className="text-lg font-bold text-orange-600">
          S/. {formatNumber(pdrTotal)}
        </div>
      </CardContent>
    </Card>
  );
};

export const FreightCard = ({ freight, setFreight, freightTotal }) => {
  return (
    <Card className="border-blue-200 bg-blue-50/30">
      <CardHeader className="py-3">
        <CardTitle className="text-sm flex items-center gap-2 text-blue-700">
          <Ship className="w-4 h-4" />
          Flete Internacional (USD)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Contenedores</Label>
            <Input
              type="number"
              min="0"
              value={freight.container_quantity || ""}
              onChange={(e) => setFreight({
                ...freight,
                container_quantity: parseInt(e.target.value) || 0
              })}
              className="h-8"
            />
          </div>
          <div>
            <Label className="text-xs">Costo/Cont. ($)</Label>
            <Input
              type="number"
              step="0.01"
              value={freight.container_cost || ""}
              onChange={(e) => setFreight({
                ...freight,
                container_cost: parseFloat(e.target.value) || 0
              })}
              className="h-8"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Costos Adicionales ($)</Label>
          <Input
            type="number"
            step="0.01"
            value={freight.additional_costs || ""}
            onChange={(e) => setFreight({
              ...freight,
              additional_costs: parseFloat(e.target.value) || 0
            })}
            className="h-8"
          />
        </div>
        <div className="text-lg font-bold text-blue-600">
          {formatCurrency(freightTotal)}
        </div>
      </CardContent>
    </Card>
  );
};

export const CostSummaryCard = ({ 
  subtotalSupply, 
  installationTotal, 
  pdrTotal, 
  freightTotal,
  otrosTotal = 0,
  otrosCurrency = "PEN",
  grandTotal 
}) => {
  const otrosSymbol = otrosCurrency === "USD" ? "$" : "S/.";
  
  return (
    <Card className="bg-slate-800 text-white">
      <CardContent className="py-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <div>
            <p className="text-xs text-slate-400">Suministro</p>
            <p className="text-lg font-bold" data-testid="summary-subtotal-supply">{formatCurrency(subtotalSupply)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Instalación</p>
            <p className="text-lg font-bold text-green-400">S/. {formatNumber(installationTotal)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">PDR</p>
            <p className="text-lg font-bold text-orange-400">S/. {formatNumber(pdrTotal)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Flete</p>
            <p className="text-lg font-bold text-blue-400">{formatCurrency(freightTotal)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400">Otros</p>
            <p className="text-lg font-bold text-purple-400">{otrosSymbol} {formatNumber(otrosTotal)}</p>
          </div>
          <div className="border-l border-slate-600 pl-4">
            <p className="text-xs text-slate-400">Total General</p>
            <p className="text-2xl font-bold text-yellow-400">{formatCurrency(grandTotal)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const CostSectionsGrid = ({
  installation,
  setInstallation,
  installationTotal,
  pdr,
  setPdr,
  pdrTotal,
  freight,
  setFreight,
  freightTotal,
  totalM2 = 0
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <InstallationCard
        installation={installation}
        setInstallation={setInstallation}
        installationTotal={installationTotal}
        totalM2={totalM2}
      />
      <PDRCard
        pdr={pdr}
        setPdr={setPdr}
        pdrTotal={pdrTotal}
      />
      <FreightCard
        freight={freight}
        setFreight={setFreight}
        freightTotal={freightTotal}
      />
    </div>
  );
};
